import { LegalInfo } from "../components/LegalInfo";
import { FooterLinks } from "../components/FooterLinks";
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AudienceToggle, PrimaryCta, StepsSection, PrivacyFaq } from "../components/LandingAudience";
import { motion } from "framer-motion";
import { Nav } from "../components/Nav";
import { useLang } from "../i18n";
import { AREA_META } from "../constants/areaMeta";
import { Button } from "../components/ui/button";

const HERO = "https://images.unsplash.com/photo-1613578519724-22fdb5d06388?crop=entropy&cs=srgb&fm=webp&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxwZWFjZWZ1bCUyMG1vcm5pbmclMjByb3V0aW5lJTIwc3VubGlnaHR8ZW58MHx8fHwxNzg0MzAxOTk3fDA&ixlib=rb-4.1.0&q=75&w=1000";

export default function Landing() {
  const { t, lang } = useLang();
  const areas = Object.keys(AREA_META);
  const [params, setParams] = useSearchParams();
  const biz = params.get("per") === "aziende";
  const setAudience = (v) => setParams(v === "aziende" ? { per: "aziende" } : {}, { replace: true });

  return (
    <div className="App relative">
      <Nav />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <AudienceToggle value={biz ? "aziende" : "persone"} onChange={setAudience} />
            <span className="inline-block text-sm font-semibold tracking-widest uppercase text-[#C81D6B] mb-4">
              {t("tagline")}
            </span>
            <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] tracking-tight text-[#2B4A59] mb-6">
              {biz ? t("hero_title_biz") : t("hero_title")}
            </h1>
            <p className="text-lg text-[#6E6B65] max-w-lg mb-8" data-testid="hero-sub">{biz ? t("hero_sub_biz") : t("hero_sub_personal")}</p>
            <div className="flex flex-wrap gap-4">
              <PrimaryCta biz={biz} testid="hero-start-btn" />
              <Link to="/login">
                <Button data-testid="hero-login-btn" variant="outline" className="rounded-full border-stone-300 px-8 h-12 text-base hover:-translate-y-0.5 transition-transform">
                  {t("cta_login")}
                </Button>
              </Link>
            </div>
            {!biz && (
              <Link to="/register" className="inline-block mt-4 text-sm text-[#B5651D] hover:underline" data-testid="hero-code-link">{t("cta_code_link")}</Link>
            )}
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }} className="relative">
            <img src={HERO} alt="morning light" width="1000" height="1500" fetchpriority="high" className="rounded-3xl w-full h-[460px] object-cover shadow-[0_20px_60px_rgba(45,74,62,0.15)]" />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-stone-100 animate-floaty max-w-[220px]">
              <p className="font-serif text-3xl text-[#3F6E85]">7 giorni</p>
              <p className="text-sm text-[#6E6B65]">{lang === "it" ? "per il tuo primo piano personalizzato" : "to your first personalized plan"}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#2B4A59] mb-10">{t("pillars_title")}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {areas.map((a, i) => {
            const Icon = AREA_META[a].icon;
            return (
              <motion.div
                key={a}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-2xl p-6 border border-stone-200 bg-white"
                style={{ backgroundColor: AREA_META[a].tint }}
              >
                <span className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: AREA_META[a].color }}>
                  <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                </span>
                <p className="font-semibold text-[#2B4A59]">{t(a)}</p>
                <p className="text-xs uppercase tracking-wider text-[#6E6B65] mt-1">
                  {t(AREA_META[a].group)}
                </p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {!biz && (
        <StepsSection title={t("how_personal_title")} testid="how-personal"
          steps={[[t("how_p1")], [t("how_p2")], [t("how_p3")]]} />
      )}

      {/* How it works */}
      {biz && <section className="bg-[#3F6E85] text-[#FDFBF7]" data-testid="how-business">
        <div className="max-w-[720px] mx-auto px-6 py-20">
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-10">{t("how_title")}</h2>
            <div className="space-y-8">
              {[1, 2, 3].map((n) => (
                <div key={n} className="flex gap-5">
                  <span className="shrink-0 w-10 h-10 rounded-full border border-[#E08A3C] text-[#FDFBF7] flex items-center justify-center font-serif text-xl">{n}</span>
                  <div>
                    <p className="text-lg font-semibold">{t(`step${n}_t`)}</p>
                    <p className="text-[#c9d3cd]">{t(`step${n}_d`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>}
      {biz && <PrivacyFaq />}

      <footer className="max-w-6xl mx-auto px-6 py-10 text-sm text-[#6E6B65] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-stone-200">
        <span>{t("brand")} — {lang === "it" ? "Coaching di benessere e longevità, senza finalità diagnostiche o terapeutiche." : "Wellness & longevity coaching, without diagnostic or therapeutic purposes."}</span>
        <FooterLinks />
      </footer>
      <LegalInfo className="max-w-6xl mx-auto" />
    </div>
  );
}
